// src/components/Estudiantes/EstudianteFaltas.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Alert,
  Spinner,
  Button,
  Row,
  Col,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";

const API_URL = "https://mixta39.dev/phpapi/api/ausencias.php";

// ---- Utilidades de fecha (LOCAL, no UTC) ----
function parseLocalDateFromISO(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).slice(0, 10); // soporta 'YYYY-MM-DD' y 'YYYY-MM-DDTHH:mm:ss'
  const [y, m, d] = s.split("-").map(Number);
  if ([y, m, d].some(Number.isNaN)) return null;
  return new Date(y, m - 1, d); // medianoche local
}
function formatLocalDateLong(dateStr) {
  const d = parseLocalDateFromISO(dateStr);
  if (!d) return dateStr ?? "";
  return d.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });
}
function yyyyMmDd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeekMonday(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay(); // 0=Dom,1=Lun,...6=Sáb
  const diff = (day + 6) % 7; // 0 si es lunes, 1 si martes, ... 6 si domingo
  date.setDate(date.getDate() - diff);
  return date; // lunes
}
function endOfWeekSunday(d) {
  const monday = startOfWeekMonday(d);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
}
function formatWeekRangeLabel(monday) {
  const sunday = endOfWeekSunday(monday);
  const optsShort = { day: "numeric", month: "short" };
  const optsYear = { year: "numeric" };
  const left = monday.toLocaleDateString("es-MX", optsShort);
  const right = sunday.toLocaleDateString("es-MX", {
    ...optsShort,
    ...optsYear,
  });
  return `Semana ${left} – ${right}`; // ej: "Semana 11 ago – 17 ago de 2025"
}
function formatMonthLabel(d) {
  return d.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
}

const EstudianteFaltas = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [faltas, setFaltas] = useState([]); // [{NombreCompleto, ApellidoPaterno, ApellidoMaterno, Turno, Grado, Grupo, FechaFalta}]

  useEffect(() => {
    let abortado = false;
    const fetchFaltas = async () => {
      setCargando(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}?EstudianteID=${encodeURIComponent(id)}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Error ${res.status}: ${txt}`);
        }
        const data = await res.json();
        if (!abortado) setFaltas(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!abortado) setError(e.message || "Error al obtener faltas.");
      } finally {
        if (!abortado) setCargando(false);
      }
    };
    fetchFaltas();
    return () => {
      abortado = true;
    };
  }, [id]);

  // Info del encabezado (una sola vez)
  const headerInfo = useMemo(() => {
    if (!faltas?.length) return null;
    const f = faltas[0];
    return {
      nombre:
        f.NombreCompleto ||
        `${f.ApellidoPaterno ?? ""} ${f.ApellidoMaterno ?? ""}`.trim(),
      grado: f.Grado ?? "",
      grupo: f.Grupo ?? "",
      turno: f.Turno ?? "",
    };
  }, [faltas]);

  // Fechas únicas normalizadas (YYYY-MM-DD) ordenadas DESC en local
  const fechasOrdenadas = useMemo(() => {
    const set = new Set(
      faltas
        .map((x) => (x.FechaFalta ?? "").slice(0, 10))
        .filter((s) => s && s.length === 10)
    );
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const da = parseLocalDateFromISO(a);
      const db = parseLocalDateFromISO(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });
    return arr;
  }, [faltas]);

  const totalFaltas = fechasOrdenadas.length;

  // ===== Resumen por MES =====
  const resumenMes = useMemo(() => {
    const map = new Map(); // key: 'YYYY-MM' -> {label, count}
    for (const fecha of fechasOrdenadas) {
      const d = parseLocalDateFromISO(fecha);
      if (!d) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = formatMonthLabel(d);
      map.set(key, { label, count: (map.get(key)?.count ?? 0) + 1 });
    }
    // ordenar DESC por key (año/mes)
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([, val]) => val);
  }, [fechasOrdenadas]);

  // ===== Resumen por SEMANA (lunes a domingo) =====
  const resumenSemana = useMemo(() => {
    const map = new Map(); // key: monday 'YYYY-MM-DD' -> {label, count}
    for (const fecha of fechasOrdenadas) {
      const d = parseLocalDateFromISO(fecha);
      if (!d) continue;
      const monday = startOfWeekMonday(d);
      const key = yyyyMmDd(monday);
      const label = formatWeekRangeLabel(monday);
      map.set(key, { label, count: (map.get(key)?.count ?? 0) + 1 });
    }
    // ordenar DESC por monday key
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([, val]) => val);
  }, [fechasOrdenadas]);

  return (
    <Container className="mt-4">
      <Row className="mb-3">
        <Col xs="auto">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Regresar
          </Button>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center">
            <span>Faltas del Estudiante</span>
            <Badge bg="danger" pill title="Total de faltas">
              {totalFaltas}
            </Badge>
          </Card.Title>

          {cargando && (
            <div className="text-center my-4">
              <Spinner animation="border" />
              <div className="mt-2">Cargando faltas...</div>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="mt-2">
              {error}
            </Alert>
          )}

          {!cargando && !error && (
            <>
              {/* Encabezado del alumno (una sola vez) */}
              {headerInfo ? (
                <Alert variant="info" className="mb-4">
                  <div className="fw-bold">{headerInfo.nombre}</div>
                  <div>
                    {headerInfo.grado && (
                      <>Grado: <strong>{headerInfo.grado}</strong> &nbsp;</>
                    )}
                    {headerInfo.grupo && (
                      <>Grupo: <strong>{headerInfo.grupo}</strong> &nbsp;</>
                    )}
                    {headerInfo.turno && (
                      <>Turno: <strong>{headerInfo.turno}</strong></>
                    )}
                  </div>
                </Alert>
              ) : (
                <Alert variant="secondary" className="mb-4">
                  No se encontró información del estudiante.
                </Alert>
              )}

              {/* Resúmenes */}
              <Row className="g-3">
                <Col md={6}>
                  <Card>
                    <Card.Header className="fw-bold">
                      Resumen por mes{" "}
                      <Badge bg="secondary">{resumenMes.length}</Badge>
                    </Card.Header>
                    <ListGroup variant="flush">
                      {resumenMes.length === 0 ? (
                        <ListGroup.Item className="text-muted">
                          — Sin datos —
                        </ListGroup.Item>
                      ) : (
                        resumenMes.map((m, idx) => (
                          <ListGroup.Item
                            key={`m-${idx}`}
                            className="d-flex justify-content-between"
                          >
                            <span>{m.label}</span>
                            <Badge bg="primary">{m.count}</Badge>
                          </ListGroup.Item>
                        ))
                      )}
                    </ListGroup>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header className="fw-bold">
                      Resumen por semana{" "}
                      <Badge bg="secondary">{resumenSemana.length}</Badge>
                    </Card.Header>
                    <ListGroup variant="flush">
                      {resumenSemana.length === 0 ? (
                        <ListGroup.Item className="text-muted">
                          — Sin datos —
                        </ListGroup.Item>
                      ) : (
                        resumenSemana.map((w, idx) => (
                          <ListGroup.Item
                            key={`w-${idx}`}
                            className="d-flex justify-content-between"
                          >
                            <span>{w.label}</span>
                            <Badge bg="primary">{w.count}</Badge>
                          </ListGroup.Item>
                        ))
                      )}
                    </ListGroup>
                  </Card>
                </Col>
              </Row>

              {/* Lista compacta de fechas */}
              <Card className="mt-3">
                <Card.Header className="fw-bold">
                  Fechas de falta <Badge bg="secondary">{totalFaltas}</Badge>
                </Card.Header>
                <ListGroup variant="flush">
                  {totalFaltas === 0 ? (
                    <ListGroup.Item className="text-muted">
                      — Sin faltas registradas —
                    </ListGroup.Item>
                  ) : (
                    fechasOrdenadas.map((fecha) => (
                      <ListGroup.Item
                        key={fecha}
                        className="d-flex justify-content-between"
                      >
                        <span>{formatLocalDateLong(fecha)}</span>
                        <span className="text-muted">{fecha}</span>
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
              </Card>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EstudianteFaltas;
